export interface AdminUser {
  readonly id: string;
  readonly display_name: string;
  readonly role_codes: readonly string[];
  readonly must_change_password?: boolean;
}

export interface AdminSession {
  readonly accessToken: string;
  readonly user: AdminUser;
  readonly authContext: 'NORMAL' | 'PASSWORD_CHANGE_RESTRICTED';
}

export interface AdminSessionStore {
  readonly read: () => AdminSession | null;
  readonly write: (session: AdminSession) => void;
  readonly clear: () => void;
  readonly subscribe: (listener: () => void) => () => void;
}

/**
 * Isolated in-memory access-token store (DG-VA-AUTH-01).
 * Production uses one module-level store per JS realm (one browser tab).
 * Tests may construct separate stores to simulate independent tabs.
 */
export function createMemoryAdminSessionStore(): AdminSessionStore {
  let session: AdminSession | null = null;
  const listeners = new Set<() => void>();

  const emit = (): void => {
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    read() {
      return session;
    },
    write(next) {
      session = next;
      emit();
    },
    clear() {
      session = null;
      emit();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const defaultAdminSessionStore = createMemoryAdminSessionStore();
let installationId: string | null = null;

export function getDefaultAdminSessionStore(): AdminSessionStore {
  return defaultAdminSessionStore;
}

export function subscribeAdminSession(listener: () => void): () => void {
  return defaultAdminSessionStore.subscribe(listener);
}

/**
 * Admin access token is memory-only (DG-VA-AUTH-01 / ADR-0032).
 * WEB refresh lives in an HttpOnly cookie, never in this module.
 */
export function readAdminSession(): AdminSession | null {
  return defaultAdminSessionStore.read();
}

export function writeAdminSession(next: AdminSession): void {
  defaultAdminSessionStore.write(next);
}

export function clearAdminSession(): void {
  defaultAdminSessionStore.clear();
}

export function readWebInstallationId(): string {
  if (installationId === null) {
    installationId = crypto.randomUUID();
  }
  return installationId;
}

export function isSystemAdmin(user: AdminUser): boolean {
  return user.role_codes.includes('SYSTEM_ADMIN');
}

export function isRestrictedAdmin(value: AdminSession): boolean {
  return (
    value.authContext === 'PASSWORD_CHANGE_RESTRICTED' || value.user.must_change_password === true
  );
}
